import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ArrowRight, Target, CheckCircle } from 'lucide-react';
import { getSiteBySlug } from '../utils/siteApi';
import { getFileUrl } from '../utils/fileUpload';
import { apiCall } from '../utils/api';
import { PUBLIC_ACCESS_CODE_VALIDATE, PUBLIC_SURVEYS } from '../config/api';

const Welcome = () => {
  const location = useLocation();
  const { siteSlug, accessCode: urlAccessCode } = useParams();
  
  // Site data state
  const [siteData, setSiteData] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const fetchingRef = useRef(false);
  const lastFetchedSlugRef = useRef(null);
  
  // Determine actual site slug - use siteSlug from URL params
  const actualSiteSlug = siteSlug;
  
  // MSQ Form state
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    confirmEmail: '',
    accessCode: urlAccessCode || ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [accessCodeValid, setAccessCodeValid] = useState(false);
  const [accessCodeVerifying, setAccessCodeVerifying] = useState(false);
  const [accessCodeData, setAccessCodeData] = useState(null);

  

  // Access code verification function
  const verifyAccessCode = async (accessCode, siteId) => {
    try {

      
      console.log('🔍 Verifying access code:', accessCode, 'for site_id:', siteId);
     
      const endpoint = PUBLIC_ACCESS_CODE_VALIDATE.replace('{access_code}', accessCode);
      console.log('🔗 Making API call to:', endpoint);
      
      const response = await apiCall(endpoint, {
        method: 'GET',
        params: { site_id: siteId }
      });
      
      //console.log('📋 Response from access code validation:', response);
      
      if (response && response.data) {
        const isValid = response.data.valid || response.data.is_valid || false;
        const accessCodeData = response.data.access_code || response.data;
        
        if (isValid && (siteId === response.data.data.access_code.site_id)) {
          console.log('✅ Access code verified:', accessCodeData);
          return { valid: true, accessCode: accessCodeData };
        }
      }
      
      console.log('❌ Access code not found or invalid');
      return { valid: false };
    } catch (error) {
      console.error('❌ Error verifying access code:', error);
      return { valid: false };
    }
  };
  
  // Update form data when URL access code changes
  useEffect(() => {
    if (urlAccessCode) {
      setFormData(prev => ({
        ...prev,
        accessCode: urlAccessCode
      }));
    }
  }, [urlAccessCode]);

  // Fetch site data based on siteSlug from URL params
  useEffect(() => {
    // Clear localStorage when Welcome page loads
    //localStorage.clear();
    console.log('🧹 localStorage cleared on Welcome page load');
    
    // Use actualSiteSlug for fetching
    const slugToFetch = actualSiteSlug;
    
    // Prevent duplicate calls for the same slug
    if (slugToFetch && (fetchingRef.current || lastFetchedSlugRef.current === slugToFetch)) {
      console.log('⏸️ Already fetching or already fetched for this slug, skipping duplicate call');
      return;
    }
    
    if (slugToFetch) {
      console.log('🔍 Detected site slug from URL params:', slugToFetch);
      
      // Mark as fetching and store the slug
      fetchingRef.current = true;
      lastFetchedSlugRef.current = slugToFetch;
      
      // Immediately fetch site data based on slug
      const fetchSiteData = async () => {
        setSiteLoading(true);
        try {
          console.log('🚀 Fetching site data for slug:', slugToFetch);
          const response = await getSiteBySlug(slugToFetch);
          
          if (response && response.data) {
            setSiteData(response.data);
            console.log('✅ Site data loaded:', response.data);
            console.log('🖼️ Logo in site data:', response.data.logo);
            console.log('🏷️ Site name in site data:', response.data.site_name);
          } else {
            console.log('❌ No site found for slug:', slugToFetch);
            setSiteData(null);
          }
        } catch (error) {
          console.error('❌ Error fetching site data:', error);
          setSiteData(null);
        } finally {
          setSiteLoading(false);
          fetchingRef.current = false;
        }
      };

      fetchSiteData();
    } else {
      // No slug in URL, reset site data
      console.log('🔄 No site slug in URL, resetting site data');
      setSiteData(null);
      setSiteLoading(false);
      lastFetchedSlugRef.current = null;
    }

    // Cleanup function to reset fetching flag if component unmounts
    return () => {
      fetchingRef.current = false;
    };
  }, [actualSiteSlug]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear validation states when user types
    if (field === 'accessCode') {
      setAccessCodeValid(false);
      setAccessCodeVerifying(false);
    }
    
    // Clear submit error when user makes any changes
    if (errors.submit) {
      setErrors(prev => ({
        ...prev,
        submit: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fname.trim()) {
      newErrors.fname = 'First name is required';
    }

    if (!formData.lname.trim()) {
      newErrors.lname = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.confirmEmail.trim()) {
      newErrors.confirmEmail = 'Please confirm your email address';
    } else if (formData.email !== formData.confirmEmail) {
      newErrors.confirmEmail = 'Email addresses do not match';
    }

    if (!formData.accessCode.trim()) {
      newErrors.accessCode = 'Access code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMSQSubmit = async (e) => {
    e.preventDefault();
    
    // Basic form validation first
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Verify access code when submit button is clicked
      if (siteData?.site_id) {
        setAccessCodeVerifying(true);
        const verification = await verifyAccessCode(formData.accessCode.trim(), siteData.site_id);
        setAccessCodeVerifying(false);
        
        if (!verification.valid) {
          setAccessCodeValid(false);
          setAccessCodeData(null);
          setErrors(prev => ({
            ...prev,
            accessCode: 'Invalid access code'
          }));
          setIsLoading(false);
          return;
        } else {
          setAccessCodeValid(true);
          setAccessCodeData(verification.accessCode);
        }
      }
      
      //console.log('Form submitted:', formData);
      //console.log('Site data:', siteData);
      
      // Generate a random 15-character session
      const session = Math.random().toString(36).substring(2, 90);
      
      // Extract parent_id and child_id from access code data
      const parent_id = accessCodeData?.parent_id || null;
      const child_id = accessCodeData?.id || null;
      
      // Submit form data to /api/public/surveys and wait for completion
      const surveyData = {
        fname: formData.fname,
        lname: formData.lname,
        email: formData.email,
        access_code: formData.accessCode,
        site_id: siteData?.site_id,
        session: session,
        parent_id: parent_id,
        child_id: child_id
      };
      
      console.log('🚀 Submitting survey data to /api/public/surveys:', surveyData);
      
      const response = await apiCall(PUBLIC_SURVEYS, {
        method: 'POST',
        data: surveyData
      });
      
      console.log('✅ Survey data submitted successfully:', response);
      
      // Survey submission completed successfully
      console.log('✅ Survey submission completed successfully');
      
      // Save all form details to localStorage
      const formStorageData = {
        fname: formData.fname,
        lname: formData.lname,
        email: formData.email,
        access_code: formData.accessCode,
        site_id: siteData?.site_id,
        session: session,
        parent_id: parent_id,
        child_id: child_id,
        site_slug: actualSiteSlug,
        submitted_at: new Date().toISOString()
      };
      
      // Store form data in localStorage
      localStorage.setItem('survey_form_data', JSON.stringify(formStorageData));
      console.log('💾 Form data saved to localStorage:', formStorageData);
      
      // Clear loading state
      setIsLoading(false);
      
      // Move to next step (MSQ Index)
      console.log('🔄 Moving to next step...');
      //window.location.href = `/msq-index?step=2&fname=${encodeURIComponent(formData.fname)}&lname=${encodeURIComponent(formData.lname)}&email=${encodeURIComponent(formData.email)}`;
      window.location.href = `/msq-index`;
    } catch (error) {
      console.error('Error during form submission:', error);
      setAccessCodeVerifying(false);
      setIsLoading(false);
      
      // Show error message to user
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to submit survey. Please try again.'
      }));
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br bg-white">

      {/* MSQ Index Form Section */}
      <section id="msq-form" className=" bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="">
          
          
          <div className="w-full">
            <div className="bg-white p-16">
              <div className="flex items-start justify-between mb-6">
                <div className="text-left flex-1 pr-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Helping you transform your marketing from a question mark into a definitive success factor for your businesses.</h3>
                  <p className="text-gray-600">
                  Take control of your marketing decisions through educational tools and a structured methodology that breeds confidence and clarity.  
                  </p>
                  <p className="text-gray-600">
                  We invite you to set aside 10 minutes of uninterrupted time to reflect and complete this survey with intention. Consider placing your devices on silent and finding a quiet spot free from distractions.
                  </p>
                  <p className="text-gray-900">Ready? Let's begin…</p>
        </div>
                
                {/* Logo on the right side */}
                <div className="flex-shrink-0">
                  {siteData?.logo ? (
                    <div className="w-40 h-40 p-2 flex items-center justify-center">
                      <img
                        src={getFileUrl(siteData.logo)} 
                        alt={`${siteData.site_name || 'Site'} Logo`}
                        className="w-full h-full object-contain"
                        onLoad={() => {
                          console.log('✅ Site logo loaded successfully:', getFileUrl(siteData.logo));
                        }}
                        onError={(e) => {
                          console.log('❌ Error loading site logo:', getFileUrl(siteData.logo));
                          console.log('🔄 Falling back to default logo');
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-3xl" style={{display: 'none'}}>
                        {siteData.site_name ? siteData.site_name.charAt(0).toUpperCase() : 'M'}
          </div>
        </div>
                  ) : (
                    <div className="w-40 h-40 bg-indigo-100 rounded-lg shadow-md flex items-center justify-center">
                      <span className="text-indigo-600 font-bold text-4xl">M</span>
          </div>
                  )}
                </div>
              </div>
              
              <form onSubmit={handleMSQSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fname" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="fname"
                      value={formData.fname}
                      onChange={(e) => handleInputChange('fname', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors.fname ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your first name"
                    />
                    {errors.fname && (
                      <p className="mt-1 text-sm text-red-600">{errors.fname}</p>
                    )}
        </div>
                  
                  <div>
                    <label htmlFor="lname" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lname"
                      value={formData.lname}
                      onChange={(e) => handleInputChange('lname', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors.lname ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your last name"
                    />
                    {errors.lname && (
                      <p className="mt-1 text-sm text-red-600">{errors.lname}</p>
                    )}
        </div>
      </div>

            <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
            </div>
            
            <div>
                  <label htmlFor="confirmEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Your Email Address
                  </label>
                  <input
                    type="email"
                    id="confirmEmail"
                    value={formData.confirmEmail}
                    onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.confirmEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirm your email address"
                  />
                  {errors.confirmEmail && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmEmail}</p>
                  )}
            </div>
            
            <div>
                  <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Access Code
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="accessCode"
                      value={formData.accessCode}
                      onChange={(e) => handleInputChange('accessCode', e.target.value)}
                      className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors.accessCode ? 'border-red-500 bg-red-50' : 
                        accessCodeValid ? 'border-green-500 bg-green-50' : 
                        accessCodeVerifying ? 'border-yellow-500 bg-yellow-50' : 
                        'border-gray-300'
                      }`}
                      placeholder="Enter your access code"
                    />
                    {(accessCodeVerifying || accessCodeValid || errors.accessCode) && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        {accessCodeValid ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : accessCodeVerifying ? (
                          <div className="w-5 h-5 border-2 border-yellow-500 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                          </div>
                        ) : errors.accessCode ? (
                          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        ) : null}
                      </div>
                    )}
                  </div>
                  
                  {/* Validation Messages */}
                  {errors.accessCode && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.accessCode}
                    </p>
                  )}
                  
                  {accessCodeValid && !errors.accessCode && (
                    <p className="mt-1 text-sm text-green-600 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Valid access code
                    </p>
                  )}
                  
                  {accessCodeVerifying && (
                    <p className="mt-1 text-sm text-yellow-600 flex items-center">
                      <svg className="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying access code...
                    </p>
                  )}
            </div>
            
            {/* Submit Error Message */}
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.submit}
                </p>
              </div>
            )}
            
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-indigo-600 text-white py-3 px-6 rounded-md font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      NEXT
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Welcome;
